<script setup>
// TODO This is a huge component, we should split this up into separate composables
// like `useSignature`, `useImageHandling`, `useFileUpload`, `useSpecialContent``
import {
  ref,
  unref,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
  useTemplateRef,
  nextTick,
} from 'vue';

import CannedResponse from '../conversation/CannedResponse.vue';
import KeyboardEmojiSelector from './keyboardEmojiSelector.vue';
import TagAgents from '../conversation/TagAgents.vue';
import TagContacts from '../conversation/TagContacts.vue';
import VariableList from '../conversation/VariableList.vue';
import SignaturePreview from '../SignaturePreview.vue';

import { useEmitter } from 'dashboard/composables/emitter';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useTrack } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';

import { BUS_EVENTS } from 'shared/constants/busEvents';
import { CONVERSATION_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import {
  MESSAGE_EDITOR_MENU_OPTIONS,
  RICH_MESSAGE_EDITOR_MENU_OPTIONS,
  MESSAGE_EDITOR_IMAGE_RESIZES,
} from 'dashboard/constants/editor';

import {
  messageSchema,
  buildEditor,
  EditorView,
  MessageMarkdownTransformer,
  MessageMarkdownSerializer,
  EditorState,
  Selection,
} from '@chatwoot/prosemirror-schema';
import { DOMSerializer, DOMParser } from 'prosemirror-model';
import {
  suggestionsPlugin,
  triggerCharacters,
} from '@chatwoot/prosemirror-schema/src/mentions/plugin';
import { cannedResponseMatcher } from './cannedResponseMatcher';

// Import extended schema for email channels
import emailSchema from './emailSchema';

import {
  findNodeToInsertImage,
  getContentNode,
  insertAtCursor,
  scrollCursorIntoView,
  setURLWithQueryAndSize,
  stripUnsupportedPasteMarks,
  handleLinkPaste,
} from 'dashboard/helper/editorHelper';
import {
  hasPressedEnterAndNotCmdOrShift,
  hasPressedCommandAndEnter,
} from 'shared/helpers/KeyboardHelpers';
import { createTypingIndicator } from '@chatwoot/utils';
import { checkFileSizeLimit } from 'shared/helpers/FileHelper';
import { uploadFile } from 'dashboard/helper/uploadHelper';
import { useI18n } from 'vue-i18n';
import { createTooltip, destroyTooltip } from 'floating-vue';
import { convertMarkdownToHtml } from '../../../helper/editorHelper';
import { processPasteContent } from 'dashboard/helper/pasteHelper';
import DOMPurify from 'dompurify';
import ImageResizeView from './ImageResizeView';

const props = defineProps({
  modelValue: { type: String, default: '' },
  editorId: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  isPrivate: { type: Boolean, default: false },
  enableSuggestions: { type: Boolean, default: true },
  overrideLineBreaks: { type: Boolean, default: false },
  updateSelectionWith: { type: String, default: '' },
  enableVariables: { type: Boolean, default: false },
  enableCannedResponses: { type: Boolean, default: true },
  variables: { type: Object, default: () => ({}) },
  enabledMenuOptions: { type: Array, default: () => [] },
  signature: { type: String, default: '' },
  allowSignature: { type: Boolean, default: false },
  channelType: { type: String, default: '' },
  showImageResizeToolbar: { type: Boolean, default: false }, // A kill switch to show or hide the image toolbar
  focusOnMount: { type: Boolean, default: true },
  targetSchema: { type: String, default: 'markdown' },
  inboxId: { type: Number, default: null },
});

const emit = defineEmits([
  'typingOn',
  'typingOff',
  'toggleUserMention',
  'toggleCannedMenu',
  'toggleVariablesMenu',
  'clearSelection',
  'blur',
  'focus',
  'input',
  'update:modelValue',
  'signatureInserted',
  'addEmailRecipient',
  'cannedResponseSelected',
]);

const { t } = useI18n();
const TYPING_INDICATOR_IDLE_TIME = 4000;
const MAXIMUM_FILE_UPLOAD_SIZE = 4; // in MB

const isHtml = computed(() => {
  return props.targetSchema === 'html' && !props.isPrivate;
});

const currentSchema = computed(() => {
  return isHtml.value ? emailSchema : messageSchema;
});

const createState = (
  content,
  placeholder,
  plugins = [],
  methods = {},
  enabledMenuOptions = []
) => {
  if (!isHtml.value && content.startsWith('\n')) {
    content = '\u00A0' + content;
  }

  let doc;
  if (isHtml.value) {
    // For HTML content, parse it directly using DOMParser
    const div = document.createElement('div');
    div.innerHTML = DOMPurify.sanitize(content || '');
    doc = DOMParser.fromSchema(currentSchema.value).parse(div);
  } else {
    // For markdown content, use the MessageMarkdownTransformer
    doc = new MessageMarkdownTransformer(currentSchema.value).parse(content);
  }

  return EditorState.create({
    doc: doc,
    plugins: buildEditor({
      schema: currentSchema.value,
      placeholder,
      methods,
      plugins,
      enabledMenuOptions,
    }),
  });
};

const { isEditorHotKeyEnabled, fetchSignatureFlagFromUISettings } =
  useUISettings();

const currentUser = useMapGetter('getCurrentUser');
const currentAccount = useMapGetter('accounts/getCurrentAccount');

const typingIndicator = createTypingIndicator(
  () => emit('typingOn'),
  () => emit('typingOff'),
  TYPING_INDICATOR_IDLE_TIME
);

// we don't need them to be reactive
// It cases weird issues where the objects are proxied
// and then the editor doesn't work as expected
// We have to wrap them in closures or use toRaw to get the actual values
let editorView = null;
let state = null;

const showUserMentions = ref(false);
const showContactMentions = ref(false);
const showCannedMenu = ref(false);
const hasCannedResults = ref(false);
const showVariables = ref(false);
const hasVariableResults = ref(false);
const showEmojiMenu = ref(false);
const mentionSearchKey = ref('');
const contactMentionSearchKey = ref('');
const cannedSearchTerm = ref('');
const variableSearchTerm = ref('');
const emojiSearchTerm = ref('');
const range = ref(null);
const isImageNodeSelected = ref(false);
const isToolbarInView = ref(true);
const toolbarPosition = ref({ top: 0, left: 0 });
const selectedImageNode = ref(null);
const selectedImagePos = ref(null);
const activeImageSizeKey = ref(null);
const sizes = MESSAGE_EDITOR_IMAGE_RESIZES;

// element ref
const editorRoot = useTemplateRef('editorRoot');
const imageUpload = useTemplateRef('imageUpload');
const editor = useTemplateRef('editor');

const transformEmptyParagraphs = doc => {
  return doc.type.create(
    doc.attrs,
    doc.content.content.map(node => {
      if (
        node.type.name === 'paragraph' &&
        node.textContent === '' &&
        node.childCount === 0
      ) {
        const nbspText = doc.type.schema.text('\u00A0');
        return node.type.create(node.attrs, nbspText);
      }
      return node;
    })
  );
};

const serializedMarkdownContent = () => {
  const transformedDoc = transformEmptyParagraphs(editorView.state.doc);
  return MessageMarkdownSerializer.serialize(transformedDoc);
};

const contentFromEditor = () => {
  if (isHtml.value) {
    const transformedDoc = transformEmptyParagraphs(editorView.state.doc);
    const fragment = DOMSerializer.fromSchema(
      currentSchema.value
    ).serializeFragment(transformedDoc.content);
    const div = document.createElement('div');
    div.appendChild(fragment);
    return div.innerHTML;
  }
  try {
    return serializedMarkdownContent();
  } catch {
    return '';
  }
};

const shouldShowVariables = computed(() => {
  return props.enableVariables && showVariables.value && !props.isPrivate;
});

const shouldShowCannedResponses = computed(() => {
  return (
    props.enableCannedResponses && showCannedMenu.value && !props.isPrivate
  );
});

const editorMenuOptions = computed(() => {
  if (props.enabledMenuOptions.length) return props.enabledMenuOptions;
  // HTML mode runs against emailSchema (which has the color/font marks) and
  // its content is rendered by the email bubble's Letter path, so styling
  // round-trips end-to-end. Plain-text channels get the basic toolbar.
  return isHtml.value
    ? RICH_MESSAGE_EDITOR_MENU_OPTIONS
    : MESSAGE_EDITOR_MENU_OPTIONS;
});

// Marks the active toolbar exposes. The schemas can parse more styling marks
// (underline, textColor, fontSize, …) than the toolbar offers buttons for, so
// pasted content could carry styling the user can neither see nor remove. The
// toolbar option names line up with the schema mark names, so intersecting them
// gives the set of marks we keep on paste; everything else is stripped.
const allowedPasteMarks = computed(() => {
  const schemaMarkNames = new Set(Object.keys(currentSchema.value.marks));
  return new Set(
    editorMenuOptions.value.filter(option => schemaMarkNames.has(option))
  );
});

function createSuggestionPlugin({
  trigger,
  minChars = 0,
  matcher = triggerCharacters(trigger, minChars),
  showMenu,
  searchTerm,
  hasResults,
  isAllowed = () => true,
}) {
  return suggestionsPlugin({
    matcher,
    suggestionClass: '',
    onEnter: args => {
      if (!isAllowed()) return false;
      showMenu.value = true;
      range.value = args.range;
      editorView = args.view;
      if (searchTerm) searchTerm.value = args.text || '';
      return false;
    },
    onChange: args => {
      editorView = args.view;
      range.value = args.range;
      if (searchTerm) searchTerm.value = args.text;
      return false;
    },
    onExit: () => {
      if (searchTerm) searchTerm.value = '';
      showMenu.value = false;
      return false;
    },
    onKeyDown: ({ event }) => {
      if (event.keyCode !== 13 || !showMenu.value) return false;
      if (hasResults && !hasResults.value) return false;
      return true;
    },
  });
}

const isEmailChannel = computed(() => {
  return props.channelType === 'Channel::Email';
});

const mentionShowMenu = computed(() => {
  if (props.isPrivate) {
    return showUserMentions;
  }
  return isEmailChannel.value ? showContactMentions : showUserMentions;
});

const mentionSearchTerm = computed(() => {
  if (props.isPrivate) {
    return mentionSearchKey;
  }
  return isEmailChannel.value ? contactMentionSearchKey : mentionSearchKey;
});

const plugins = computed(() => {
  if (!props.enableSuggestions) {
    return [];
  }

  return [
    createSuggestionPlugin({
      trigger: '@',
      showMenu: mentionShowMenu.value,
      searchTerm: mentionSearchTerm.value,
      isAllowed: () => props.isPrivate || isEmailChannel.value,
    }),
    createSuggestionPlugin({
      trigger: '/',
      matcher: cannedResponseMatcher,
      showMenu: showCannedMenu,
      searchTerm: cannedSearchTerm,
      hasResults: hasCannedResults,
      isAllowed: () => !props.isPrivate,
    }),
    createSuggestionPlugin({
      trigger: '{{',
      showMenu: showVariables,
      searchTerm: variableSearchTerm,
      hasResults: hasVariableResults,
      isAllowed: () => !props.isPrivate,
    }),
    createSuggestionPlugin({
      trigger: ':',
      minChars: 2,
      showMenu: showEmojiMenu,
      searchTerm: emojiSearchTerm,
    }),
  ];
});

const signatureEnabled = computed(() => {
  // this is considered the source of truth, we watch this property
  // on change, we toggle the signature in the editor

  if (!props.isPrivate && props.channelType) {
    if (!(currentAccount.value?.allow_toggle_signature ?? true)) {
      return true;
    }

    if (props.allowSignature) {
      return fetchSignatureFlagFromUISettings(props.channelType);
    }
  }

  return false;
});

watch(showUserMentions, updatedValue => {
  emit('toggleUserMention', props.isPrivate && updatedValue);
});
watch(showContactMentions, updatedValue => {
  emit(
    'toggleUserMention',
    !props.isPrivate && isEmailChannel.value && updatedValue
  );
});
watch(showCannedMenu, updatedValue => {
  emit('toggleCannedMenu', !props.isPrivate && updatedValue);
});
// The reply box blocks its Enter hotkey while this menu is reported open, so
// an empty list must not count as open.
watch([showVariables, hasVariableResults], ([updatedValue, hasResults]) => {
  emit('toggleVariablesMenu', !props.isPrivate && updatedValue && hasResults);
});

function focusEditorInputField(pos = 'end') {
  const activeElement = document.activeElement;
  if (
    activeElement &&
    ((activeElement.tagName === 'INPUT' && activeElement.type === 'text') ||
      activeElement.tagName === 'TEXTAREA')
  ) {
    return;
  }
  const { tr } = editorView.state;

  const selection =
    pos === 'end' ? Selection.atEnd(tr.doc) : Selection.atStart(tr.doc);

  editorView.dispatch(tr.setSelection(selection));
  editorView.focus();
}

function isBodyEmpty(content) {
  // if content is undefined, we assume that the body is empty
  if (!content) return true;

  // trimming should remove all the whitespaces, so we can check the length
  if (isHtml.value) {
    const div = document.createElement('div');
    div.innerHTML = DOMPurify.sanitize(content);
    const textContent = div.textContent || div.innerText || '';
    return textContent.trim().length === 0;
  }
  return content.trim().length === 0;
}

function focusEditor() {
  if (props.disabled) return;

  focusEditorInputField('end');
}

function openFileBrowser() {
  imageUpload.value.click();
}

// Track DOM elements that have a floating-vue tooltip attached so we can
// detach them on teardown / re-creation of the menubar.
const tooltippedEls = new Set();

const applyTooltip = (el, content) => {
  if (!el || !content) return;
  if (tooltippedEls.has(el)) destroyTooltip(el);
  el.removeAttribute('title');
  createTooltip(el, content, {});
  tooltippedEls.add(el);
};

const detachTooltip = el => {
  if (!el) return;
  if (tooltippedEls.has(el)) {
    destroyTooltip(el);
    tooltippedEls.delete(el);
  }
};

const destroyAllTooltips = () => {
  tooltippedEls.forEach(el => destroyTooltip(el));
  tooltippedEls.clear();
};

// Upstream prosemirror-menu sets `title` on each rendered menu item via DOM
// API — we can't intercept it. Post-mount we walk the menubar, lift the
// native title onto a floating-vue tooltip, and remove the attribute so we
// don't see two tooltips on hover.
const enhanceMenubarTooltips = () => {
  nextTick(() => {
    const menubar = editorRoot.value?.querySelector('.ProseMirror-menubar');
    if (!menubar) return;
    menubar.querySelectorAll('[title]').forEach(el => {
      const text = el.getAttribute('title');
      if (text) applyTooltip(el, text);
    });
  });
};

function reloadState(content = props.modelValue) {
  // The new state gets new plugin instances, and the old suggestion plugin is dropped without
  // onExit: an open picker would keep a range into the old document, and picking an item then
  // crashes the editor (RangeError: Position N out of range).
  range.value = null;
  showUserMentions.value = false;
  showContactMentions.value = false;
  showCannedMenu.value = false;
  showVariables.value = false;
  showEmojiMenu.value = false;

  const unrefContent = unref(content);

  state = createState(
    unrefContent,
    props.placeholder,
    plugins.value,
    {
      onImageUpload: openFileBrowser,
      t,
      attachTooltip: applyTooltip,
      detachTooltip,
    },
    editorMenuOptions.value
  );

  editorView.updateState(state);
  focusEditor();
}
function setToolbarPosition() {
  const editorRect = editorRoot.value.getBoundingClientRect();
  const rect = selectedImageNode.value.getBoundingClientRect();

  const proseMirrorEl = editorRoot.value.querySelector('.ProseMirror');
  const visibleRect = proseMirrorEl
    ? proseMirrorEl.getBoundingClientRect()
    : editorRect;

  const clipParent =
    editorRoot.value.closest('.reply-box') ||
    editorRoot.value.closest('.new-message');
  const clipRect = clipParent ? clipParent.getBoundingClientRect() : null;

  const inProseMirror =
    rect.bottom > visibleRect.top && rect.top < visibleRect.bottom;

  const toolbarBottom = rect.bottom + 30;
  const footerEl =
    clipParent?.querySelector('.new-message-footer') ||
    clipParent?.querySelector('.reply-message-footer');
  const footerHeight = footerEl ? footerEl.getBoundingClientRect().height : 0;
  const inContainer = clipRect
    ? rect.bottom > clipRect.top &&
      toolbarBottom < clipRect.bottom - footerHeight
    : true;

  isToolbarInView.value = inProseMirror && inContainer;

  const imgLeft = rect.left - editorRect.left;
  const imgCenterX = imgLeft + rect.width / 2;

  toolbarPosition.value = {
    top: `${rect.top - editorRect.top + rect.height + 4}px`,
    left: `${imgCenterX}px`,
    transform: 'translateX(-50%)',
  };
}

function onScrollUpdateToolbar() {
  if (isImageNodeSelected.value && selectedImageNode.value) {
    setToolbarPosition();
  }
}

function detectActiveImageSize(imgEl) {
  if (isHtml.value) {
    const widthStyle = imgEl.style.maxWidth;
    if (!widthStyle) return 'ORIGINAL';
    const match = sizes.find(s => s.width && widthStyle === s.width);
    return match?.key ?? null;
  }
  const heightStyle = imgEl.style.height;
  if (!heightStyle) return 'ORIGINAL';
  const match = sizes.find(
    s => s.height !== 'auto' && heightStyle === s.height
  );
  return match?.key ?? null;
}

function setURLWithQueryAndImageSize(size) {
  if (!props.showImageResizeToolbar) {
    return;
  }
  setURLWithQueryAndSize(
    selectedImageNode.value,
    size,
    editorView,
    selectedImagePos.value,
    props.targetSchema === 'html'
  );
  activeImageSizeKey.value = size.key;

  nextTick(() => {
    selectedImageNode.value = document.querySelector(
      'img.ProseMirror-selectednode'
    );
    if (selectedImageNode.value) {
      setToolbarPosition();
    } else {
      isImageNodeSelected.value = false;
    }
  });
}

function isEditorMouseFocusedOnAnImage() {
  if (!props.showImageResizeToolbar) {
    return;
  }
  selectedImageNode.value = document.querySelector(
    'img.ProseMirror-selectednode'
  );
  if (selectedImageNode.value) {
    isImageNodeSelected.value = true;
    selectedImagePos.value = editorView.state.selection.from;
    activeImageSizeKey.value = detectActiveImageSize(selectedImageNode.value);
    setToolbarPosition();
  } else {
    isImageNodeSelected.value = false;
    selectedImagePos.value = null;
    activeImageSizeKey.value = null;
  }
}

function emitOnChange() {
  emit('input', contentFromEditor());
  emit('update:modelValue', contentFromEditor());
}

function updateImgToolbarOnDelete() {
  // check if the selected node is present or not on keyup
  // this is needed because the user can select an image and then delete it
  // in that case, the selected node will be null and we need to hide the toolbar
  // otherwise, the toolbar will be visible even when the image is deleted and cause some errors
  if (selectedImageNode.value) {
    const hasImgSelectedNode = document.querySelector(
      'img.ProseMirror-selectednode'
    );
    if (!hasImgSelectedNode) {
      isImageNodeSelected.value = false;
    }
  }
}

function isEnterToSendEnabled() {
  return isEditorHotKeyEnabled('enter');
}

function isCmdPlusEnterToSendEnabled() {
  return isEditorHotKeyEnabled('cmd_enter');
}

useKeyboardEvents({
  'Alt+KeyP': {
    action: focusEditorInputField,
    allowOnFocusedInput: true,
  },
  'Alt+KeyL': {
    action: focusEditorInputField,
    allowOnFocusedInput: true,
  },
});

function onImageInsertInEditor(fileUrl) {
  const { tr } = editorView.state;

  const insertData = findNodeToInsertImage(editorView.state, fileUrl);

  if (insertData) {
    editorView.dispatch(
      tr.insert(insertData.pos, insertData.node).scrollIntoView()
    );
    focusEditorInputField();
  }
}

async function uploadImageToStorage(file) {
  try {
    const { fileUrl } = await uploadFile(file);
    if (fileUrl) {
      onImageInsertInEditor(fileUrl);
    }
  } catch (error) {
    useAlert(t('CONVERSATION.IMAGE_UPLOAD_ERROR'));
  }
}

function onFileChange() {
  const file = imageUpload.value.files[0];
  if (checkFileSizeLimit(file, MAXIMUM_FILE_UPLOAD_SIZE)) {
    uploadImageToStorage(file);
  } else {
    useAlert(
      t('CONVERSATION.FILE_SIZE_LIMIT', {
        MAXIMUM_SUPPORTED_FILE_UPLOAD_SIZE: MAXIMUM_FILE_UPLOAD_SIZE,
      })
    );
  }

  imageUpload.value = '';
}

function handleLineBreakWhenEnterToSendEnabled(event) {
  if (
    hasPressedEnterAndNotCmdOrShift(event) &&
    isEnterToSendEnabled() &&
    !props.overrideLineBreaks
  ) {
    event.preventDefault();
  }
}

async function insertNodeIntoEditor(node, from = 0, to = 0) {
  state = insertAtCursor(editorView, node, from, to);
  emitOnChange();
  await nextTick();
  scrollCursorIntoView(editorView);
}

function insertContentIntoEditor(content, defaultFrom = 0) {
  const currentContent = contentFromEditor();
  const isCurrentContentEmpty = isBodyEmpty(currentContent);

  if (isCurrentContentEmpty) {
    if (isHtml.value) {
      content = convertMarkdownToHtml(content);
    }
    reloadState(content);
    focusEditorInputField('end');
  } else {
    let slice;
    if (isHtml.value) {
      const htmlContent = convertMarkdownToHtml(content);
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = DOMPurify.sanitize(htmlContent);
      slice = DOMParser.fromSchema(currentSchema.value).parseSlice(tempDiv);
    } else {
      const node = new MessageMarkdownTransformer(currentSchema.value).parse(
        content
      );
      const from = defaultFrom || editorView.state.selection.from || 0;
      slice = node.slice(from, node.content.size);
    }
    const { tr } = editorView.state;
    editorView.dispatch(tr.replaceSelection(slice).scrollIntoView());
  }
  emitOnChange();
}

function insertSignatureIntoEditor(content) {
  const currentContent = contentFromEditor();
  const isCurrentContentEmpty = isBodyEmpty(currentContent);
  const contentWithNewline = isHtml.value ? '<br>' + content : '\n' + content;

  if (isCurrentContentEmpty) {
    reloadState(contentWithNewline);
    focusEditorInputField('start');
  } else {
    let slice;
    if (isHtml.value) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = DOMPurify.sanitize(contentWithNewline);
      slice = DOMParser.fromSchema(currentSchema.value).parseSlice(tempDiv);
    } else {
      const node = new MessageMarkdownTransformer(currentSchema.value).parse(
        contentWithNewline
      );
      slice = node.slice(0, node.content.size);
    }

    const { tr } = editorView.state;
    const endPos = tr.doc.content.size;
    editorView.dispatch(tr.insert(endPos, slice.content).scrollIntoView());
  }
  emitOnChange();
  emit('signatureInserted');
}

/**
 * Inserts special content (mention, canned response, variable, emoji) into the editor.
 * @param {string} type - The type of special content to insert. Possible values: 'mention', 'canned_response', 'variable', 'emoji'.
 * @param {Object|string} content - The content to insert, depending on the type.
 */
function insertSpecialContent(type, content) {
  if (!editorView) {
    return;
  }

  let { node, from, to } = getContentNode(
    editorView,
    type,
    content,
    range.value,
    props.variables
  );

  if (!node) return;

  insertNodeIntoEditor(node, from, to);

  const event_map = {
    mention: CONVERSATION_EVENTS.USED_MENTIONS,
    cannedResponse: CONVERSATION_EVENTS.INSERTED_A_CANNED_RESPONSE,
    variable: CONVERSATION_EVENTS.INSERTED_A_VARIABLE,
    emoji: CONVERSATION_EVENTS.INSERTED_AN_EMOJI,
  };

  useTrack(event_map[type]);
}

function onContactSelect(contact) {
  if (contact.email && isEmailChannel.value) {
    emit('addEmailRecipient', contact.email);
  }

  insertSpecialContent('mention', contact);
}

function handleLineBreakWhenCmdAndEnterToSendEnabled(event) {
  if (
    hasPressedCommandAndEnter(event) &&
    isCmdPlusEnterToSendEnabled() &&
    !props.overrideLineBreaks
  ) {
    event.preventDefault();
  }
}

function onKeydown(event) {
  if (isEnterToSendEnabled()) {
    handleLineBreakWhenEnterToSendEnabled(event);
  }
  if (isCmdPlusEnterToSendEnabled()) {
    handleLineBreakWhenCmdAndEnterToSendEnabled(event);
  }
}

function createEditorView() {
  const nodeViews = props.showImageResizeToolbar
    ? {
        image: (node, view, getPos) =>
          new ImageResizeView(node, view, getPos, {
            useStyleWidth: props.targetSchema === 'html',
            onResizeEnd: () => nextTick(isEditorMouseFocusedOnAnImage),
          }),
      }
    : {};

  editorView = new EditorView(editor.value, {
    state: state,
    editable: () => !props.disabled,
    nodeViews,
    transformPasted: slice =>
      stripUnsupportedPasteMarks(slice, allowedPasteMarks.value),
    // A URL pasted over selected text links that text instead of replacing it.
    // Gated on the toolbar exposing a link, so the shortcut can only produce
    // markup the agent could have written by hand.
    handlePaste: (view, event, slice) =>
      !props.disabled &&
      allowedPasteMarks.value.has('link') &&
      handleLinkPaste(view, event, slice),
    dispatchTransaction: tx => {
      state = state.apply(tx);
      editorView.updateState(state);
      if (tx.docChanged) {
        emitOnChange();
      }
    },
    handleDOMEvents: {
      keyup: () => {
        if (!props.disabled) {
          typingIndicator.start();
          updateImgToolbarOnDelete();
        }
      },
      keydown: (view, event) => !props.disabled && onKeydown(event),
      focus: () => !props.disabled && emit('focus'),
      click: () => !props.disabled && isEditorMouseFocusedOnAnImage(),
      blur: () => {
        if (props.disabled) return;
        typingIndicator.stop();
        emit('blur');
      },
      paste: (_view, event) => {
        if (props.disabled) return;

        const data = event.clipboardData.files;
        if (data.length > 0) {
          Array.from(data).forEach(file => {
            if (file.type.startsWith('image/')) {
              uploadImageToStorage(file);
            }
          });
          event.preventDefault();
          return;
        }

        const htmlContent = event.clipboardData.getData('text/html');
        if (htmlContent) {
          const pasteBehavior =
            currentUser.value?.ui_settings?.paste_behavior || 'preserve_all';

          if (pasteBehavior !== 'preserve_all') {
            const processedContent = processPasteContent(
              htmlContent,
              pasteBehavior
            );
            const newClipboardData = new DataTransfer();

            newClipboardData.setData('text/html', processedContent);

            Object.defineProperty(event, 'clipboardData', {
              value: newClipboardData,
              writable: false,
            });
          }
        }
      },
    },
  });
}

watch(
  computed(() => props.modelValue),
  (newVal = '') => {
    if (newVal !== contentFromEditor()) {
      reloadState(newVal);
    }
  }
);

watch(
  computed(() => props.editorId),
  () => {
    cannedSearchTerm.value = '';
    reloadState(props.modelValue);
  }
);

watch(
  computed(() => props.targetSchema),
  () => {
    reloadState(props.modelValue);
  }
);

watch(
  computed(() => props.showImageResizeToolbar),
  () => {
    destroyAllTooltips();
    reloadState(props.modelValue);
    editorView.destroy();
    createEditorView();
    editorView.updateState(state);
    enhanceMenubarTooltips();
  }
);

watch(
  computed(() => props.updateSelectionWith),
  (newValue, oldValue) => {
    if (!editorView) return;

    if (newValue !== oldValue) {
      if (props.updateSelectionWith !== '') {
        const node = editorView.state.schema.text(props.updateSelectionWith);

        const tr = editorView.state.tr.replaceSelectionWith(node);
        editorView.focus();
        state = editorView.state.apply(tr);
        editorView.updateState(state);
        emitOnChange();
        emit('clearSelection');
      }
    }
  }
);

onMounted(() => {
  // [VITE] state assignment was done in created before
  state = createState(
    props.modelValue,
    props.placeholder,
    plugins.value,
    {
      onImageUpload: openFileBrowser,
      t,
      attachTooltip: applyTooltip,
      detachTooltip,
    },
    editorMenuOptions.value
  );

  createEditorView();
  editorView.updateState(state);
  if (props.focusOnMount) {
    focusEditorInputField();
  }
  enhanceMenubarTooltips();

  const scrollParent =
    editorRoot.value?.closest('.reply-box') ||
    editorRoot.value?.closest('.new-message')?.parentElement ||
    editorRoot.value;
  scrollParent?.addEventListener('scroll', onScrollUpdateToolbar, true);
  window.addEventListener('resize', onScrollUpdateToolbar);
});

onBeforeUnmount(() => {
  destroyAllTooltips();
  const scrollParent =
    editorRoot.value?.closest('.reply-box') ||
    editorRoot.value?.closest('.new-message')?.parentElement ||
    editorRoot.value;
  scrollParent?.removeEventListener('scroll', onScrollUpdateToolbar, true);
  window.removeEventListener('resize', onScrollUpdateToolbar);
});

// BUS Event to insert text or markdown into the editor at the
// current cursor position.
// Components using this
// 1. SearchPopover.vue
useEmitter(BUS_EVENTS.INSERT_INTO_RICH_EDITOR, insertContentIntoEditor);

defineExpose({
  focusEditor,
});
</script>

<template>
  <div ref="editorRoot" class="relative w-full">
    <TagAgents
      v-if="showUserMentions && isPrivate"
      :search-key="mentionSearchKey"
      @select-agent="content => insertSpecialContent('mention', content)"
    />
    <TagContacts
      v-if="showContactMentions && !isPrivate && isEmailChannel"
      :search-key="contactMentionSearchKey"
      @select-contact="onContactSelect"
    />
    <CannedResponse
      v-if="shouldShowCannedResponses"
      :search-key="cannedSearchTerm"
      :inbox-id="inboxId"
      @replace="
        (content, meta) => {
          insertSpecialContent('cannedResponse', content);
          emit('cannedResponseSelected', meta);
        }
      "
      @has-results="hasCannedResults = $event"
    />
    <VariableList
      v-if="shouldShowVariables"
      :search-key="variableSearchTerm"
      @has-results="hasVariableResults = $event"
      @select-variable="content => insertSpecialContent('variable', content)"
    />
    <KeyboardEmojiSelector
      v-if="showEmojiMenu"
      :search-key="emojiSearchTerm"
      @select-emoji="emoji => insertSpecialContent('emoji', emoji)"
    />
    <input
      ref="imageUpload"
      type="file"
      accept="image/png, image/jpeg, image/jpg, image/gif, image/webp"
      hidden
      @change="onFileChange"
    />
    <div ref="editor" class="editor-mount" />
    <SignaturePreview
      :html-signature="signature"
      :enabled="signatureEnabled"
      :editor-id="editorId"
      :allow-toggle-signature="currentAccount?.allow_toggle_signature ?? true"
      @insert-signature="insertSignatureIntoEditor"
    />
    <div
      v-show="isImageNodeSelected && showImageResizeToolbar && isToolbarInView"
      class="absolute shadow-md rounded-[4px] flex gap-1 py-1 px-1 bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-50"
      :style="{
        top: toolbarPosition.top,
        left: toolbarPosition.left,
        transform: toolbarPosition.transform,
      }"
    >
      <button
        v-for="size in sizes"
        :key="size.key"
        class="text-xs font-medium rounded-[4px] border border-solid px-1.5 py-0.5"
        :class="
          activeImageSizeKey === size.key
            ? 'bg-woot-500 dark:bg-woot-500 text-white border-woot-500 dark:border-woot-500'
            : 'border-slate-200 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800'
        "
        @click="setURLWithQueryAndImageSize(size)"
      >
        {{ t(`CONVERSATION.IMAGE_RESIZE.${size.key}`) }}
      </button>
    </div>
    <slot name="footer" />
  </div>
</template>

<style lang="scss">
@import '@chatwoot/prosemirror-schema/src/styles/base.scss';

.ProseMirror-menubar-wrapper {
  @apply flex flex-col;

  .ProseMirror-menubar {
    min-height: var(--space-two) !important;
    @apply -ml-2.5 pb-0 bg-transparent text-n-slate-11;

    .ProseMirror-menu-active {
      @apply bg-slate-75 dark:bg-slate-800;
    }
  }

  > .ProseMirror {
    @apply p-0 break-words text-slate-800 dark:text-slate-100;

    h1,
    h2,
    h3,
    h4,
    h5,
    h6,
    p {
      @apply text-slate-800 dark:text-slate-100;
    }

    // Tailwind's preflight resets headings to body size; restore visual
    // hierarchy so the heading dropdown actually changes how text looks.
    h1 {
      @apply text-2xl font-bold my-2;
    }

    h2 {
      @apply text-xl font-bold my-2;
    }

    h3 {
      @apply text-lg font-semibold my-1.5;
    }

    blockquote {
      @apply border-slate-400 dark:border-slate-500;

      p {
        @apply text-slate-600 dark:text-slate-400;
      }
    }

    // Room for two- and three-digit numbers ("10.", "100."). prosemirror-schema's
    // base.scss gives lists only 16px, and the editor has no padding and scrolls,
    // so a wider number was cut off ("0." for item 10).
    ol {
      @apply pl-8;
    }

    ol li {
      @apply list-item list-decimal;
    }
  }
}

.conversation-footer {
  .reply-box {
    .ProseMirror-woot-style {
      @apply overflow-auto min-h-[5rem];
      max-height: var(--dynamic-reply-editor-max-height, 5rem);
    }

    &.custom-height {
      .reply-box__top > .input {
        @apply flex flex-col min-h-0;
      }

      .editor-mount {
        @apply flex-1 min-h-0 flex flex-col;
      }

      .ProseMirror-menubar-wrapper {
        @apply flex flex-col flex-1 min-h-0;
      }

      .ProseMirror-menubar {
        @apply shrink-0;
      }

      .ProseMirror-woot-style {
        flex: 1 1 0;
        max-height: none;
      }
    }

    &.is-popout {
      .ProseMirror-woot-style {
        max-height: var(--dynamic-reply-editor-max-height-full, 5rem);
      }
    }
  }
}

.new-message {
  .ProseMirror-woot-style {
    @apply overflow-auto min-h-[5rem];
    max-height: var(--dynamic-editor-max-height, 5rem);
  }
}

.ProseMirror-prompt {
  @apply z-[9999] bg-n-alpha-3 backdrop-blur-[100px] border border-n-strong p-6 shadow-xl rounded-xl;

  h5 {
    @apply text-n-slate-12 mb-1.5;
  }

  .ProseMirror-prompt-buttons {
    button {
      @apply h-8 px-3;

      &[type='submit'] {
        @apply bg-n-brand text-white hover:bg-n-brand/90;
      }

      &[type='button'] {
        @apply bg-n-slate-9/10 text-n-slate-12 hover:bg-n-slate-9/20;
      }
    }
  }
}

.is-private {
  .prosemirror-mention-node {
    @apply font-medium bg-n-amber-2/80 dark:bg-n-amber-2/80 text-n-slate-12 py-0 px-1;
  }

  .ProseMirror-menubar-wrapper {
    > .ProseMirror {
      @apply text-n-slate-12;

      p {
        @apply text-n-slate-12;
      }
    }
  }
}

.editor-wrap {
  @apply mb-4;
}

.message-editor {
  @apply rounded-lg outline outline-1 outline-n-weak hover:outline-n-slate-6 dark:hover:outline-n-slate-6 bg-n-alpha-black2 py-0 px-1 mb-0;
}

.editor_warning {
  @apply outline outline-1 outline-n-ruby-8 dark:outline-n-ruby-8 hover:outline-n-ruby-9 dark:hover:outline-n-ruby-9;
}

.editor-warning__message {
  @apply text-red-400 dark:text-red-400 font-normal text-sm pt-1 pb-0 px-0;
}

.image-resize-wrapper {
  display: inline-block;
  position: relative;
  line-height: 0;

  .image-resize-overlay {
    position: absolute;
    border: 2px dashed theme('colors.woot.500');
    background: rgba(0, 0, 0, 0.05);
    pointer-events: none;
    z-index: 9;

    .image-resize-handle {
      position: absolute;
      bottom: -6px;
      right: -6px;
      width: 10px;
      height: 10px;
      background: theme('colors.woot.500');
      border: 1px solid white;
      border-radius: 2px;
      cursor: nwse-resize;
      pointer-events: auto;
    }

    .image-resize-size-label {
      position: absolute;
      bottom: 4px;
      right: 4px;
      background: theme('colors.woot.500');
      color: white;
      font-size: 11px;
      line-height: 1;
      padding: 3px 6px;
      border-radius: 3px;
      white-space: nowrap;
      font-variant-numeric: tabular-nums;
    }
  }
}

.prosemirror-image-resizing .ProseMirror {
  user-select: none;
}
</style>
